import type { Option } from "@waldner-laboreinrichtungen/component-system";
import {
  Button,
  Dropdown,
  FlexBox,
  space,
  Table,
  Tag,
  theme,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import type { i18n as I18n, TFunction } from "i18next";
import type { FC, MutableRefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import styledComponent from "styled-components";

import { ReactComponent as ErrorIcon } from "../../public/material-icons/error.svg";
import { ReactComponent as FiberIcon } from "../../public/material-icons/fiber-manual-record.svg";
import { ReactComponent as InfoIcon } from "../../public/material-icons/info.svg";
import { ReactComponent as TimelapseIcon } from "../../public/material-icons/timelapse.svg";
import { useAccessToken } from "../auth-provider";
import { updateIncidentStates, useIncidents } from "../hooks/use-incidents";
import {
  findIncidentChannel,
  formatIncidentMessage,
} from "../util/incident-message";
import { logger } from "../util/logger";
import {
  formatDateWithSuffix,
  getTimeDifference,
} from "../util/time-formatter";
import useOnScreen from "../util/use-on-screen";
import type { Channel, Device, Incident, Unit } from "../view-model";
import { ErrorReportModal } from "./error-report-modal";

// Eine Episode ist "pending" (offen, dünn-grau), solange noch eine Aktion
// aussteht. Aktiv: muss quittiert + gemeldet sein. Aufgelöst: non-strict gilt
// als erledigt, strict erst nach Bestätigung.
const isIncidentPending = (incident: Incident): boolean => {
  if (incident.active) {
    return !incident.acknowledged || !incident.reported;
  }
  if (incident.strictAudited === false) {
    return false;
  }
  return !incident.confirmed;
};

// User-facing Message: erst i18n über den vollen Namen, dann über den
// FU-relativen Kurznamen, dann der formatierte Grenzwert-Text, sonst die rohe
// OPC-UA-Message bzw. ein generischer Fallback.
const resolveIncidentMessage = (
  incident: Incident,
  t: TFunction,
  i18n: I18n,
  channelLabel?: string
): string => {
  if (i18n.exists(`${incident.name}.message`, { ns: "incidents" })) {
    return t(`${incident.name}.message`, { ns: "incidents" });
  }
  const shortName = incident.name.split(".").slice(1).join(".");
  if (i18n.exists(`${shortName}.message`, { ns: "incidents" })) {
    return t(`${shortName}.message`, { ns: "incidents" });
  }
  if (incident.limitState) {
    return formatIncidentMessage(incident, t, channelLabel);
  }
  return incident.message || t("unknown.message");
};

export type DetailIncidentsProps = {
  device: Device;
  /** FUs des Geräts — deren `functions` sind die Channel-Quelle für die
   * Label-Auflösung (device.channels ist im LADS-Flow nicht befüllt). */
  units?: Unit[];
};

const StyledDetailIncidents = styledComponent.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${space(6)};
`;

// const StyledCode = styledComponent.span`
//   font-family: ui-monospace, SFMono-Regular, monospace;
//   font-size: ${theme.font.size.default.xs};
//   color: ${theme.colorPalette.grey500};
//   letter-spacing: 0.02em;
// `;

const StyledMessage = styledComponent.span<{ pending?: boolean }>`
  font-weight: ${(p) => (p.pending ? 600 : 400)};
  color: ${(p) =>
    p.pending ? theme.colorPalette.black : theme.colorPalette.grey600};
`;

export const LadsDetailIncidents: FC<DetailIncidentsProps> = ({
  device,
  units,
}) => {
  // Channel-Pool über alle FUs — Quelle für die kettenaufgelösten Labels
  // (ComponentName → displayName → browseName) in den Meldungstexten.
  const incidentChannels: Channel[] = (units ?? []).flatMap((unit) =>
    Object.values(unit.functions ?? {})
  );
  const { i18n, t } = useTranslation();
  const token = useAccessToken();

  const typeOptions: Option[] = [
    {
      id: 0,
      label: t("incident.type.all"),
      value: "all",
    },
    {
      id: 1,
      label: t("incident.type.critical"),
      value: "critical",
    },
    {
      id: 2,
      label: t("incident.type.warning"),
      value: "warning",
    },
  ];

  // Lifecycle-orientierter Filter: "open" = Confirm steht noch aus
  // (egal ob aktiv, quittiert, gemeldet oder schon behoben aber unbestätigt),
  // "resolved" = Operator hat bestätigt → Vorgang erledigt.
  const statusOptions: (Option & { emptyText?: string })[] = [
    {
      id: 0,
      label: t("incident.filter.all"),
      value: "all",
      emptyText: t("incident.empty.all") as string,
    },
    {
      id: 1,
      label: t("incident.filter.open"),
      value: "open",
      emptyText: t("incident.empty.open") as string,
    },
    {
      id: 2,
      label: t("incident.filter.resolved"),
      value: "resolved",
      emptyText: t("incident.empty.resolved") as string,
    },
  ];

  const [selectedType, setSelectedType] = useState(typeOptions[0]);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[1]);

  // Filter werden SERVER-side angewandt (severity + lifecycle), damit die
  // gefetchte Liste exakt der angezeigten entspricht — Voraussetzung für
  // korrektes Infinite Scroll. Der lifecycle-Filter kennt die strict-Logik
  // (open = noch nicht erledigt, resolved = erledigt) im Backend.
  let severityFilter: "ERROR" | "WARNING" | undefined;
  if (selectedType.value === "critical") {
    severityFilter = "ERROR";
  } else if (selectedType.value === "warning") {
    severityFilter = "WARNING";
  }
  let lifecycleFilter: "open" | "resolved" | undefined;
  if (selectedStatus.value === "open") {
    lifecycleFilter = "open";
  } else if (selectedStatus.value === "resolved") {
    lifecycleFilter = "resolved";
  }

  // Device-scoped infinite scroll — same hook + paging mechanism as the global
  // error history, just restricted to this device. Further pages load as the
  // sentinel row at the list bottom scrolls into view.
  const {
    incidents: apiIncidents,
    isLoading,
    isLoadingMore = false,
    pagesTotal,
    setSize,
    size,
  } = useIncidents(
    undefined,
    undefined,
    device,
    severityFilter,
    lifecycleFilter
  );
  const [incidents, setIncidents] = useState(apiIncidents);

  const loadMoreComponent = useRef<
    MutableRefObject<HTMLTableRowElement>
  >() as unknown as MutableRefObject<HTMLTableRowElement>;
  const isVisible = useOnScreen(loadMoreComponent);

  useEffect(() => {
    if (isVisible && !isLoadingMore && pagesTotal && size < pagesTotal) {
      setSize(size + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, isLoadingMore]);

  useEffect(() => {
    setIncidents(apiIncidents);
  }, [apiIncidents]);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(
    {} as Incident
  );

  // Keep the modal's incident view in sync with the live store
  useEffect(() => {
    if (!selectedIncident?.id) return;
    const fresh = incidents.find((i) => i.id === selectedIncident.id);
    if (fresh && fresh !== selectedIncident) {
      setSelectedIncident(fresh);
    }
  }, [incidents, selectedIncident]);

  useEffect(() => {
    setSelectedType(
      typeOptions.find((item) => item.id === selectedType.id) as Option
    );
    setSelectedStatus(
      statusOptions.find((item) => item.id === selectedStatus.id) as Option
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, t]);

  // Kein Client-Filter mehr — severity + lifecycle werden server-side gefiltert
  // (siehe useIncidents oben). Die gefetchte Liste IST die anzuzeigende.

  const openModal = (incident: Incident) => {
    setSelectedIncident(incident);
    setIsOpenModal(true);
  };

  return (
    <StyledDetailIncidents>
      {selectedIncident && (
        <ErrorReportModal
          channels={incidentChannels}
          device={device}
          incident={selectedIncident as Incident}
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          updateIncidentStates={async (
            incident,
            modalDevice,
            action,
            comment
          ) => {
            await updateIncidentStates({
              comment,
              incident,
              device: modalDevice,
              action,
              token: token ?? "",
            });
            let flagKey = "reported";
            if (action === "ack") {
              flagKey = "acknowledged";
            } else if (action === "confirm") {
              flagKey = "confirmed";
            }
            setIncidents((prev) =>
              prev?.map((i) =>
                i.id === incident.id
                  ? { ...i, [flagKey]: true, comment: comment ?? i.comment }
                  : i
              )
            );
          }}
        />
      )}
      <FlexBox gap={space(4)}>
        <Dropdown
          options={typeOptions}
          selectedOption={selectedType}
          setSelectedOption={setSelectedType}
        />
        <Dropdown
          options={statusOptions}
          selectedOption={selectedStatus}
          setSelectedOption={setSelectedStatus}
        />
      </FlexBox>

      <Table
        emptyState={
          !isLoading &&
          incidents?.length === 0 && (
            <FlexBox
              alignItems="center"
              gap={space(2)}
              height="100%"
              justifyContent="center"
              position="absolute"
              width="100%"
            >
              <InfoIcon color={theme.color.primaryLight} fontSize={30} />
              <Typography size="lg" weight="semiBold">
                {selectedStatus.emptyText}
              </Typography>
            </FlexBox>
          )
        }
      >
        <Table.Head>
          <Table.Row>
            <Table.Header columnWidth="60px">
              {t(`header.severity`)}
            </Table.Header>
            <Table.Header columnWidth="100px">
              {t(`header.status`)}
            </Table.Header>
            {/* <Table.Header columnWidth="100px">
              {t(`header.errorcode`)}
            </Table.Header> */}
            <Table.Header>{t(`header.message`)}</Table.Header>
            <Table.Header columnWidth="200px">{t(`header.date`)}</Table.Header>
            <Table.Header columnWidth="140px" />
          </Table.Row>
        </Table.Head>
        {isLoading ? (
          <Table.Body>
            {Array.from({ length: 5 }).map((_element, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Table.Row key={index}>
                <Table.Data>
                  <Skeleton circle height={20} width={20} />
                </Table.Data>
                <Table.Data>
                  <Skeleton />
                </Table.Data>
                <Table.Data>
                  <Skeleton />
                </Table.Data>
                <Table.Data>
                  <Skeleton count={2} />
                </Table.Data>
                <Table.Data>
                  <Skeleton />
                </Table.Data>
              </Table.Row>
            ))}
          </Table.Body>
        ) : (
          <Table.Body>
            {incidents?.map((incident) => {
              const listSeverity = incident.maxSeverity ?? incident.severity;
              const isCritical = listSeverity === "critical";
              const SeverityIcon = isCritical ? ErrorIcon : FiberIcon;
              const severityColor = isCritical
                ? theme.color.error
                : theme.color.warning;
              return (
                <Table.Row
                  key={incident.id}
                  onClick={() => openModal(incident)}
                >
                  <Table.Data>
                    <SeverityIcon
                      color={severityColor}
                      fontSize={20}
                      title={t(`incident.type.${listSeverity}`) as string}
                    />
                  </Table.Data>
                  <Table.Data>
                    {incident.active && (
                      <Tag minor variant="neutral">
                        {t(`incident.status.active`)}
                      </Tag>
                    )}
                  </Table.Data>
                  {/* <Table.Data>
                  <StyledCode>#{incident.code}</StyledCode>
                </Table.Data> */}
                  <Table.Data>
                    <StyledMessage
                      /*
                      Fett = noch eine Operator-Aktion offen, die jetzt
                      gerade möglich ist:
                        - aktiv:    Quittieren oder Melden ausstehend
                        - inaktiv:  Bestätigen ausstehend
                      Sobald in der jeweiligen Phase nichts mehr zu tun
                      ist, wird die Zeile dünn-grau.
                    */
                      pending={isIncidentPending(incident)}
                    >
                      {resolveIncidentMessage(
                        incident,
                        t,
                        i18n,
                        findIncidentChannel(incident, incidentChannels)?.name
                      )}
                    </StyledMessage>
                  </Table.Data>
                  <Table.Data>
                    <FlexBox flexDirection="column" gap={space(1)}>
                      <Typography color="black" size="sm">
                        {formatDateWithSuffix(new Date(incident.createdAt))}
                      </Typography>
                      <FlexBox alignItems="center" gap={space(1)}>
                        <TimelapseIcon
                          color={theme.colorPalette.grey400}
                          fontSize={12}
                        />
                        <Typography color="grey500" size="xs">
                          {incident.closedAt
                            ? getTimeDifference(
                                new Date(incident.createdAt),
                                new Date(incident.closedAt)
                              )
                            : getTimeDifference(
                                new Date(incident.createdAt),
                                new Date()
                              )}
                        </Typography>
                      </FlexBox>
                    </FlexBox>
                  </Table.Data>
                  <Table.Data textAlign="end">
                    <FlexBox
                      alignItems="flex-end"
                      flexDirection="column"
                      gap={space(1)}
                    >
                      {!(
                        incident.strictAudited === false &&
                        incident.active === false
                      ) &&
                        !incident.acknowledged &&
                        !incident.confirmed && (
                          // Wrapper stoppt die Propagation (Button.onClick liefert
                          // kein Event), sonst öffnet der Zeilen-Klick das Modal.
                          <div onClick={(e) => e.stopPropagation()}>
                            <Button
                              onClick={() => {
                                void updateIncidentStates({
                                  incident,
                                  device,
                                  action: "ack",
                                  token: token ?? "",
                                })
                                  .then(() => {
                                    setIncidents((prev) =>
                                      prev?.map((i) =>
                                        i.id === incident.id
                                          ? { ...i, acknowledged: true }
                                          : i
                                      )
                                    );
                                  })
                                  .catch((error: unknown) => {
                                    logger.error("acknowledge failed", error);
                                  });
                              }}
                              type="button"
                              variant="primary"
                            >
                              {t("incident.acknowledge")}
                            </Button>
                          </div>
                        )}
                      {incident.reported && (
                        <Tag minor variant="info">
                          {t("incident.state.reported")}
                        </Tag>
                      )}
                      {/* "Quittiert" nur solange aktiv — bei "Behoben" überflüssig. */}
                      {incident.acknowledged && incident.active !== false && (
                        <Tag minor variant="info">
                          {t("incident.state.acknowledged")}
                        </Tag>
                      )}
                      {incident.confirmed && (
                        <Tag minor variant="info">
                          {t("incident.state.confirmed")}
                        </Tag>
                      )}
                      {incident.active === false && (
                        <Tag minor variant="info">
                          {t("incident.state.fixed")}
                        </Tag>
                      )}
                    </FlexBox>
                  </Table.Data>
                </Table.Row>
              );
            })}
            {/* Lädt die nächste Seite, sobald diese Zeile in den Viewport scrollt. */}
            {isLoadingMore && (incidents?.length ?? 0) > 0 && (
              <Table.Row>
                <Table.Data>
                  <Skeleton />
                </Table.Data>
                <Table.Data>
                  <Skeleton />
                </Table.Data>
                <Table.Data>
                  <Skeleton count={2} />
                </Table.Data>
                <Table.Data>
                  <Skeleton />
                </Table.Data>
                <Table.Data>
                  <Skeleton />
                </Table.Data>
              </Table.Row>
            )}
            {(incidents?.length ?? 0) > 0 && <tr ref={loadMoreComponent} />}
          </Table.Body>
        )}
      </Table>
    </StyledDetailIncidents>
  );
};
